import { useState, type FormEvent } from "react";
import { Cpu, MemoryStick, HardDrive, CheckCircle, Archive, Wifi } from "lucide-react";
import { ServiceTierSelector } from "@/components/vm/ServiceTierSelector";
import { PricingSummaryPanel } from "@/components/vm/PricingSummaryPanel";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { usePricingRule } from "@/hooks/useBilling";
import type { ServiceTier, NetworkSpeed, VMCreatePayload, Template, CapacityInfo } from "@/types";
import type { BackupPlan } from "@/types/billing";

interface VMProvisionFormProps {
  templates: Template[];
  capacity: CapacityInfo[];
  isSubmitting: boolean;
  onSubmit: (payload: VMCreatePayload) => void;
}

const TEMPLATES_DEFAULT: Template[] = [
  {
    id: "debian-12-cloud",
    name: "Debian 12",
    os: "debian",
    description: "Stable, minimal Debian Bookworm",
    min_cores: 1,
    min_memory_mb: 512,
    min_disk_gb: 8,
  },
  {
    id: "ubuntu-2404-cloud",
    name: "Ubuntu 24.04 LTS",
    os: "ubuntu",
    description: "Latest Ubuntu LTS with cloud-init",
    min_cores: 1,
    min_memory_mb: 1024,
    min_disk_gb: 10,
  },
];

const BACKUP_PLANS: { value: BackupPlan; label: string; description: string }[] = [
  { value: "none", label: "No Backup", description: "No automated backup — manual only" },
  { value: "snapshot", label: "Snapshot", description: "Daily snapshot with short retention" },
  { value: "full", label: "Full Backup", description: "Weekly full backup with PBS" },
  { value: "incremental", label: "Incremental", description: "Daily incremental + weekly full" },
];

export function VMProvisionForm({
  templates: templatesProp,
  capacity,
  isSubmitting,
  onSubmit,
}: VMProvisionFormProps) {
  const templates =
    templatesProp.length > 0 ? templatesProp : TEMPLATES_DEFAULT;

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]?.id ?? "");
  const [tier, setTier] = useState<ServiceTier>("A");
  const [hostname, setHostname] = useState("");
  const [cpuCores, setCpuCores] = useState(2);
  const [ramMb, setRamMb] = useState(2048);
  const [diskGb, setDiskGb] = useState(20);
  const [networkSpeed, setNetworkSpeed] = useState<NetworkSpeed>("100m");
  const [backupPlan, setBackupPlan] = useState<BackupPlan>("none");

  const { data: pricingRule } = usePricingRule();

  const template = templates.find((t) => t.id === selectedTemplate);

  // Capacity check
  const totalAvailableCpu = capacity.reduce(
    (sum, n) => sum + (n.total_cpu - n.used_cpu),
    0
  );
  const totalAvailableRam = capacity.reduce(
    (sum, n) => sum + (n.total_ram_mb - n.used_ram_mb),
    0
  );
  const totalAvailableDisk = capacity.reduce(
    (sum, n) => sum + (n.total_disk_gb - n.used_disk_gb),
    0
  );

  const hasCapacity =
    capacity.length === 0 ||
    (cpuCores <= totalAvailableCpu &&
      ramMb <= totalAvailableRam &&
      diskGb <= totalAvailableDisk);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: hostname,
      template: selectedTemplate,
      service_tier: tier,
      cores: cpuCores,
      memory_mb: ramMb,
      disk_gb: diskGb,
      network_speed: networkSpeed,
    });
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
      {/* ─── Left: Form ─── */}
      <form onSubmit={handleSubmit} className="space-y-8 lg:col-span-2">
        {/* 1. Template Selection */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-gray-200">
            1. Select Template
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {templates.map((tmpl) => (
              <button
                key={tmpl.id}
                type="button"
                onClick={() => setSelectedTemplate(tmpl.id)}
                className={`rounded-xl border-2 p-4 text-left transition-all ${
                  selectedTemplate === tmpl.id
                    ? "border-blue-500 bg-blue-500/5"
                    : "border-gray-800 hover:border-gray-700"
                }`}
              >
                <h3 className="font-semibold text-gray-200">{tmpl.name}</h3>
                <p className="mt-1 text-sm text-gray-400">{tmpl.description}</p>
                <p className="mt-2 text-xs text-gray-500">
                  Min: {tmpl.min_cores} CPU, {tmpl.min_memory_mb} MB RAM,{" "}
                  {tmpl.min_disk_gb} GB Disk
                </p>
              </button>
            ))}
          </div>
        </section>

        {/* 2. Service Tier / Topology */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-gray-200">
            2. Topology
          </h2>
          <ServiceTierSelector value={tier} onChange={setTier} />
        </section>

        {/* 3. Resources */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-gray-200">
            3. Resources
          </h2>
          <div className="space-y-6 rounded-xl border border-gray-800 bg-gray-900/50 p-6">
            {/* CPU Slider */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <Cpu className="h-4 w-4 text-blue-400" />
                  CPU Cores
                </label>
                <span className="rounded bg-gray-800 px-2 py-0.5 text-sm font-mono text-gray-200">
                  {cpuCores}
                </span>
              </div>
              <input
                type="range"
                min={template?.min_cores ?? 1}
                max={16}
                step={1}
                value={cpuCores}
                onChange={(e) => setCpuCores(Number(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-blue-500 bg-gray-700 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-lg"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>{template?.min_cores ?? 1}</span>
                <span>16</span>
              </div>
            </div>

            {/* RAM Slider */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <MemoryStick className="h-4 w-4 text-purple-400" />
                  RAM (MB)
                </label>
                <span className="rounded bg-gray-800 px-2 py-0.5 text-sm font-mono text-gray-200">
                  {ramMb >= 1024
                    ? `${(ramMb / 1024).toFixed(1)} GB`
                    : `${ramMb} MB`}
                </span>
              </div>
              <input
                type="range"
                min={template?.min_memory_mb ?? 256}
                max={32768}
                step={256}
                value={ramMb}
                onChange={(e) => setRamMb(Number(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-purple-500 bg-gray-700 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-lg"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>{template?.min_memory_mb ?? 256} MB</span>
                <span>32 GB</span>
              </div>
            </div>

            {/* Disk Slider */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <HardDrive className="h-4 w-4 text-green-400" />
                  Disk (GB)
                </label>
                <span className="rounded bg-gray-800 px-2 py-0.5 text-sm font-mono text-gray-200">
                  {diskGb} GB
                </span>
              </div>
              <input
                type="range"
                min={template?.min_disk_gb ?? 4}
                max={500}
                step={5}
                value={diskGb}
                onChange={(e) => setDiskGb(Number(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-green-500 bg-gray-700 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-lg"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-500">
                <span>{template?.min_disk_gb ?? 4} GB</span>
                <span>500 GB</span>
              </div>
            </div>

            {/* Network Speed */}
            <div>
              <label className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-300">
                <Wifi className="h-4 w-4 text-cyan-400" />
                Network Speed
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    { value: "100m", label: "100 Mbit/s", desc: "Standard" },
                    { value: "500m", label: "500 Mbit/s", desc: "Enhanced" },
                    { value: "1g", label: "1 Gbit/s", desc: "Premium" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNetworkSpeed(opt.value)}
                    className={`rounded-lg border-2 p-3 text-center transition-all ${
                      networkSpeed === opt.value
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-gray-700 hover:border-gray-600"
                    }`}
                  >
                    <p className={`text-sm font-semibold ${networkSpeed === opt.value ? "text-cyan-400" : "text-gray-200"}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 4. Backup Plan */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-gray-200">
            4. Backup Plan
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {BACKUP_PLANS.map((plan) => (
              <button
                key={plan.value}
                type="button"
                onClick={() => setBackupPlan(plan.value)}
                className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                  backupPlan === plan.value
                    ? "border-blue-500 bg-blue-500/5"
                    : "border-gray-800 hover:border-gray-700"
                }`}
              >
                <Archive
                  className={`mt-0.5 h-5 w-5 ${
                    backupPlan === plan.value
                      ? "text-blue-400"
                      : "text-gray-500"
                  }`}
                />
                <div>
                  <h3 className="font-semibold text-gray-200">{plan.label}</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    {plan.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* 5. Hostname */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-gray-200">
            5. Hostname
          </h2>
          <input
            type="text"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder="e.g. web-prod-01"
            required
            pattern="[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9]"
            className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Letters, numbers, and hyphens only. Must start and end with
            alphanumeric.
          </p>
        </section>

        {/* Capacity Check */}
        {capacity.length > 0 && (
          <section>
            <h2 className="mb-4 text-lg font-semibold text-gray-200">
              Capacity Check
            </h2>
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">CPU Available</p>
                  <p
                    className={
                      cpuCores <= totalAvailableCpu
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {totalAvailableCpu} cores ({cpuCores} requested)
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">RAM Available</p>
                  <p
                    className={
                      ramMb <= totalAvailableRam
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {(totalAvailableRam / 1024).toFixed(1)} GB (
                    {(ramMb / 1024).toFixed(1)} GB requested)
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Disk Available</p>
                  <p
                    className={
                      diskGb <= totalAvailableDisk
                        ? "text-green-400"
                        : "text-red-400"
                    }
                  >
                    {totalAvailableDisk} GB ({diskGb} GB requested)
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Submit */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={isSubmitting || !hasCapacity || !hostname}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <LoadingSpinner size="sm" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            {isSubmitting ? "Provisioning..." : "Provision VM"}
          </button>
          {!hasCapacity && (
            <p className="text-sm text-red-400">
              Insufficient cluster capacity for the requested resources.
            </p>
          )}
        </div>
      </form>

      {/* ─── Right: Pricing Panel (sticky) ─── */}
      <div className="lg:sticky lg:top-6 lg:self-start">
        <PricingSummaryPanel
          pricingRule={pricingRule ?? null}
          cores={cpuCores}
          ramMb={ramMb}
          diskGb={diskGb}
          tier={tier}
          networkSpeed={networkSpeed}
          backupPlan={backupPlan}
        />
      </div>
    </div>
  );
}
